/**
 * PATH: packages/ui/src/components/settings/sender-profiles/SenderProfilesSection.tsx
 * FILE_ID: UI-SETTINGS-SENDERS-027
 * PURPOSE: The counterparty stewardship queue (Settings -> Email ->
 *          Senders) — what the classifier believes each sender domain IS
 *          (Supplier / Client / ...), with one-tap accept/correct/reject.
 * WHY: W3-V8-P3 — 1,900+ learned edges had no human surface; MDM golden
 *      records rot without a stewardship loop.
 * FLOW: mount -> GET /kanban/senders/ -> rows -> verdict POST -> reload row.
 * LANDMINE: verdicts are suggest-layer (they teach the memory) — no email
 *      is re-routed by a click here, so no confirm dialogs needed.
 * BACKEND: quotech-backend email_kanban/views/senders.py (cross-repo).
 * RELATED: EMAILHUB-FEATURE-COMPONENTS-ROLECHIP-001 (chip showing the role).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Loader2, Search, UsersRound } from 'lucide-react'
import { cn } from '../../../lib/utils'
import type { SettingsApiClient } from '../types'
import type { SenderProfile, SenderRoleOption } from './types'
import {
  fetchSenderProfiles,
  sendSenderVerdict,
  setSenderCompany,
} from './senderProfilesApi'
import { SenderProfileRow } from './SenderProfileRow'
import { PersonMergeQueue } from './PersonMergeQueue'
import { PersonContextPanel } from './PersonContextPanel'

const PAGE_SIZE = 25

export interface SenderProfilesSectionProps {
  apiClient?: SettingsApiClient
  className?: string
}

export function SenderProfilesSection({
  apiClient,
  className,
}: SenderProfilesSectionProps) {
  const [profiles, setProfiles] = useState<SenderProfile[]>([])
  const [roleOptions, setRoleOptions] = useState<SenderRoleOption[]>([])
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  // W3-V8 — which domain's people panel is open (one at a time).
  const [expandedDomain, setExpandedDomain] = useState<string | null>(null)
  const [roleFilter, setRoleFilter] = useState('')
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const load = useCallback(
    async (opts: { search: string; role: string; offset: number }) => {
      if (!apiClient) {
        setLoading(false)
        return
      }
      setLoading(true)
      setError(null)
      try {
        const data = await fetchSenderProfiles(apiClient, {
          search: opts.search,
          role: opts.role,
          limit: PAGE_SIZE,
          offset: opts.offset,
        })
        setProfiles(data.profiles)
        setTotal(data.total)
        if (data.roles.length) setRoleOptions(data.roles)
      } catch {
        setError('Could not load sender profiles.')
      } finally {
        setLoading(false)
      }
    },
    [apiClient],
  )

  useEffect(() => {
    load({ search: '', role: '', offset: 0 })
  }, [load])

  // Debounced search — reads are cheap but keystrokes are cheaper.
  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value)
      setOffset(0)
      if (searchTimer.current) clearTimeout(searchTimer.current)
      searchTimer.current = setTimeout(() => {
        load({ search: value, role: roleFilter, offset: 0 })
      }, 300)
    },
    [load, roleFilter],
  )

  const handleRoleFilter = useCallback(
    (value: string) => {
      setRoleFilter(value)
      setOffset(0)
      load({ search, role: value, offset: 0 })
    },
    [load, search],
  )

  const handlePage = useCallback(
    (nextOffset: number) => {
      setOffset(nextOffset)
      load({ search, role: roleFilter, offset: nextOffset })
    },
    [load, search, roleFilter],
  )

  const handleVerdict = useCallback(
    async (
      domain: string,
      kind: 'accept' | 'correct' | 'reject',
      role: string,
      chosenRole?: string,
    ) => {
      if (!apiClient) return
      const key = `${domain}:${role}:${kind}`
      setBusyKey(key)
      setError(null)
      try {
        await sendSenderVerdict(apiClient, {
          domain,
          kind,
          role,
          ...(chosenRole ? { chosen_role: chosenRole } : {}),
        })
        // Reload the current page — the verdict may re-rank roles.
        await load({ search, role: roleFilter, offset })
      } catch {
        setError('Could not record the verdict.')
      } finally {
        setBusyKey(null)
      }
    },
    [apiClient, load, search, roleFilter, offset],
  )

  // 029 — correct/attach the CRM company a domain maps to.
  const handleCompanySave = useCallback(
    async (domain: string, clientName: string) => {
      if (!apiClient) return
      setBusyKey(`${domain}:company`)
      setError(null)
      try {
        const result = await setSenderCompany(apiClient, {
          domain,
          client_name: clientName,
        })
        if (!result.anchored) {
          setError(
            `Linked ${domain} to ${result.client_name}, but the client's email ` +
              'addresses are on other domains — the link may not stick. ' +
              'Update the client email in the CRM.',
          )
        }
        await load({ search, role: roleFilter, offset })
      } catch {
        setError('Could not save the company.')
      } finally {
        setBusyKey(null)
      }
    },
    [apiClient, load, search, roleFilter, offset],
  )

  if (!apiClient) return null

  const showingFrom = total === 0 ? 0 : offset + 1
  const showingTo = Math.min(offset + PAGE_SIZE, total)

  return (
    <div className={cn('space-y-4 max-w-2xl pt-2', className)}>
      <div className="border-t border-slate-200 dark:border-slate-700 pt-5">
        <div className="flex items-center gap-2">
          <UsersRound className="w-4 h-4 text-slate-500" />
          <h3 className="text-base font-semibold text-slate-900 dark:text-white">
            Senders
          </h3>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          What the AI has learned each sender is to your business
          (Supplier, Client, Subcontractor, ...). Confirming or correcting
          a role teaches the classifier; it never moves existing email.
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search a domain or address"
            className="w-full pl-8 pr-3 py-1.5 rounded-md text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => handleRoleFilter(e.target.value)}
          aria-label="Filter by role"
          className="px-2 py-1.5 rounded-md text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200"
        >
          <option value="">All roles</option>
          {roleOptions.map((o) => (
            <option key={o.role} value={o.role}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="text-xs text-red-600 bg-red-50 dark:bg-red-900/20 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      <section className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-400 px-3 py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading sender profiles…
          </div>
        ) : profiles.length === 0 ? (
          <p className="text-xs text-slate-400 px-3 py-4">
            {search || roleFilter
              ? 'No sender profiles match this filter.'
              : 'No learned sender profiles yet. They appear as the classifier processes mail.'}
          </p>
        ) : (
          profiles.map((p) => (
            <div key={p.domain}>
              <SenderProfileRow
                profile={p}
                roleOptions={roleOptions}
                busyKey={busyKey}
                onVerdict={(kind, role, chosenRole) =>
                  handleVerdict(p.domain, kind, role, chosenRole)
                }
                onCompanySave={(clientName) =>
                  handleCompanySave(p.domain, clientName)
                }
                peopleExpanded={expandedDomain === p.domain}
                onTogglePeople={() =>
                  setExpandedDomain((cur) =>
                    cur === p.domain ? null : p.domain,
                  )
                }
              />
              {/* W3-V8 — the people behind the domain, lazy on expand. */}
              {expandedDomain === p.domain && apiClient && (
                <div className="px-3 pb-2.5 border-b border-slate-100 dark:border-slate-800 last:border-b-0">
                  <PersonContextPanel apiClient={apiClient} domain={p.domain} />
                </div>
              )}
            </div>
          ))
        )}
      </section>

      {/* Pager — total can be 1,900+ domains; keep the page bounded. */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>
            Showing {showingFrom}-{showingTo} of {total}
          </span>
          <div className="flex gap-1.5">
            <button
              onClick={() => handlePage(Math.max(offset - PAGE_SIZE, 0))}
              disabled={offset === 0 || loading}
              className="px-2.5 py-1 rounded-md border border-slate-200 dark:border-slate-700 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              onClick={() => handlePage(offset + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= total || loading}
              className="px-2.5 py-1 rounded-md border border-slate-200 dark:border-slate-700 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* W3-V8-P4 — self-suppressing: invisible until Splink queues pairs. */}
      <PersonMergeQueue apiClient={apiClient} />
    </div>
  )
}

export default SenderProfilesSection
